import { useCallback, useState } from "react";

import { BillingDetailsDialog } from "../Pages/Plan/BillingDetailsDialog";
import { forgetStripeReturn, rememberStripeReturn } from "./stripeReturn.js";

// 400 from any endpoint that returns a Stripe URL: the agency has no fiscal data yet
const BILLING_DETAILS_REQUIRED = "billing_details_required";

const leave = (url, expectation) => {
  if (!url) throw new Error("missing_url");
  if (expectation) rememberStripeReturn(expectation);
  else forgetStripeReturn();
  window.location.assign(url);
};

/**
 * `go(request, expectation)` sends the agency to the Stripe URL that `request` resolves. Without fiscal data it
 * opens the details modal (render `detailsDialog`) and retries after saving. Resolves true when leaving, false
 * if the modal is closed; rejects with the request error.
 */
export function useStripeRedirect() {
  const [pending, setPending] = useState(null);

  const go = useCallback(
    (request, expectation = null) =>
      new Promise((resolve, reject) => {
        request()
          .then((url) => {
            leave(url, expectation);
            resolve(true);
          })
          .catch((error) => {
            if (error?.response?.data?.code !== BILLING_DETAILS_REQUIRED) {
              reject(error);
              return;
            }
            setPending({ request, expectation, resolve, reject });
          });
      }),
    []
  );

  const closeDetails = () => {
    pending.resolve(false);
    setPending(null);
  };

  const retry = async () => {
    try {
      leave(await pending.request(), pending.expectation);
      pending.resolve(true);
    } catch (error) {
      pending.reject(error);
      setPending(null);
    }
  };

  const detailsDialog = pending ? <BillingDetailsDialog onClose={closeDetails} onSaved={retry} /> : null;

  return { go, detailsDialog };
}
